import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Text, Grid } from "@geist-ui/core";
import { PlayCircle, Book, Map, MessageCircle, Headphones } from "@geist-ui/icons";
import PatreonLoginButton from '../../auth/PatreonLoginButton';
import { useAuth } from '../../../context/AuthContext';
import './Landing.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, isPatron } = useAuth();
  
  // If already authenticated and is a patron or has forced access, navigate to bookshelf
  const handlePlay = () => {
    if (user && isPatron()) {
      navigate('/bookshelf');
    } else if (user) {
      // User is logged in but not a patron and doesn't have forced access
      navigate('/subscription-required');
    } else {
      // Not logged in, show Patreon login
      window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/patreon`;
    }
  };

  return (
    <div className="landing-page">
      {/* Hero section - keep as is */}
      <div className="container hero-container">
        <div className="col">
          <Link to="/" className="navbar-logo">
            <div className="logo-container">
              <img src="https://pub-3cbdf0fe4d9c40f19241eb1a5a52005c.r2.dev/om-assets/chest.webp" alt="Chester icon" className="chester-icon" />
              <Text span>ObviousMimic.AI</Text>
            </div>
          </Link>

          <div className="content-wrapper">
            <Text h1>Play your own 5e character.</Text> 
            <Text h1>On a Solo VTT.<sup style={{ 
              fontSize: '0.5em', 
              marginLeft: '2px',
              color: '#ded8d1' 
            }}>beta</sup></Text> 
            
            <Text p><b>Obvious Mimic</b> brings 5e solo adventures to life. Choose your own path with an ereader, a solo VTT, audiobook integration, an AI assistant DM, and more. 
            <p>Join our exclusive <a href="https://www.patreon.com/obviousmimic" target="_blank" rel="noopener noreferrer" className="underlined-link">Patreon beta</a> to shape the evolution of solo tabletop gaming!</p></Text> 

            <div className="buttons">
              {user && isPatron() ? (
                <Button 
                  onClick={() => navigate('/bookshelf')}
                  className="theme-button theme-button--w100 theme-button--cta"
                >
                  <Text span>Play Now!</Text> 
                </Button>
              ) : (
                <PatreonLoginButton className="theme-button--w100" />
              )}

              <a 
                href="https://vimeo.com/1055237278" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', width: '100%' }}
              >
                <Button 
                  icon={<PlayCircle />} 
                  className="theme-button theme-button--w100"
                >
                  <Text span>Watch video</Text> 
                </Button>
              </a>              
            </div>
          </div>
        </div>

        <div className="col">
          <Link to="/" className="navbar-logo navbar-logo--responsive">
            <div className="logo-container">
              <img src="https://pub-3cbdf0fe4d9c40f19241eb1a5a52005c.r2.dev/om-assets/chest.webp" alt="Chester icon" className="chester-icon" />
              <Text span>ObviousMimic.AI</Text>
            </div>
          </Link>

          <div className="hero-image">
            <img src="https://pub-3cbdf0fe4d9c40f19241eb1a5a52005c.r2.dev/om-assets/chest.webp" alt="Chester the AI"/>
          </div>
        </div>
      </div>  

      {/* Feature sections */}
      <section className="feature-section">
        <div className="feature-container">
          <div className="section-header">
            <Text h2>Everything You Need for Solo D&D Adventures</Text>
            <Text p className="section-subtitle">
              One platform, multiple tools designed for solo tabletop RPG players
            </Text>
          </div>

          <div className="feature-grid">
            <div className="feature-card">
              <Book size={48} />
              <Text h3>Interactive eReader</Text>
              <Text p>Navigate through branching storylines with our easy-to-use reader interface. Make choices that matter in your solo adventure.</Text>
            </div>

            <div className="feature-card">
              <Map size={48} />
              <Text h3>Simplified Virtual Tabletop</Text>
              <Text p>Intuitive battle maps with tokens, enemy stat blocks, and drawing tools. Perfect for more tactical combat encounters.</Text>
            </div>

            <div className="feature-card">
              <Headphones size={48} />
              <Text h3>Audiobook Integration</Text>
              <Text p>Listen to narration that syncs perfectly with the adventure text. Or switch on the ambient music made for each adventure.</Text>
            </div>

            <div className="feature-card">
              <MessageCircle size={48} />
              <Text h3>AI Dungeon Master</Text>
              <Text p>Chester, your AI assistant, answers rules questions and enhances your solo gaming experience.</Text>
            </div>
          </div>
        </div>
      </section>

      <section className="feature-showcase">
        <div className="feature-container">
          <div className="feature-row">
            <div className="feature-content">
              <Text h2>Immersive Story Experience</Text>
              <Text p>Our interactive reader presents branching storylines where your choices determine the outcome. Each decision you make shapes your adventure, creating a truly personalized experience.</Text>
              <ul className="feature-list">
                <li>Pick-your-path 5e adventures with automatic progress tracking</li>
                <li>A tooltip glossary of rules and in-game lore</li>
                <li>Built-in audiobook narration and music</li>
                <li>Adjustable reading preferences and built-in dice rolling</li>
              </ul>
            </div>
            <div className="feature-image">
              <img src="/assets/marketing-image-reader.webp" alt="Interactive Reader" />
            </div>
          </div>
        </div>
      </section>

      <section className="feature-showcase reverse">
        <div className="feature-container">
          <div className="feature-row">
            <div className="feature-image">
              <img src="/assets/marketing-image-VTT.webp" alt="Virtual Tabletop" />
            </div>
            <div className="feature-content">
              <Text h2>Tactical Virtual Tabletop</Text>
              <Text p>Our virtual tabletop is designed specifically for solo play. Place tokens, measure distances, track hit points, and visualize combat encounters with ease.</Text>
              <ul className="feature-list">
                <li>Intuitive token movement</li>
                <li>Dice rolling</li>
                <li>Drawing capabilities</li>
                <li>Enemy stat block displays</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="feature-container">
          <Text h2>Start Your Solo Adventure Today</Text>
          <Text p>Join our growing community of solo tabletop RPG enthusiasts and experience D&D like never before.</Text>
          <div className="cta-button-container">
            {user && isPatron() ? (
              <Button 
                onClick={() => navigate('/bookshelf')} 
                className="theme-button theme-button--large theme-button--cta"
              >
                <Text span>Play Now</Text>
              </Button>
            ) : (
              <PatreonLoginButton className="theme-button--large" />
            )}
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="feature-container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src="https://pub-3cbdf0fe4d9c40f19241eb1a5a52005c.r2.dev/om-assets/chest.webp" alt="Chester icon" className="chester-icon" />
              <Text span>ObviousMimic.AI</Text>
            </div>
            <Text p>© {new Date().getFullYear()} Obvious Mimic AI. All rights reserved.</Text>
            <div className="footer-links">
              <a href="https://www.patreon.com/obviousmimic" target="_blank" rel="noopener noreferrer">Patreon</a>
              <a href="https://vimeo.com/1055237278" target="_blank" rel="noopener noreferrer">Demo Video</a>
              <a href="https://obviousmimic.com" target="_blank" rel="noopener noreferrer">Made by Obvious Mimic</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
